import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material';
import { Router } from '@angular/router';
import { SatPopover } from '@ncstate/sat-popover';

import { ServiceRepairResult } from '../../shared/models/service-repair-result.model';
import { State } from '../../shared/state-view/state.model';
import { SearchQuery } from '../models/search-query.model';
import { storageAvailable } from '../../util/helpers';

/**
 * Results list for the Service/Repair Finder, mirroring {@link FboFinderResultsListingComponent}. Renders the
 * paged result cards (each linking to the existing `/business/:id` detail page), a filter popover, and relays
 * hover events for map sync.
 */
@Component({
  selector: 'app-service-repair-finder-results-listing',
  templateUrl: './service-repair-finder-results-listing.component.html',
  styleUrls: ['./service-repair-finder-results-listing.component.scss']
})
export class ServiceRepairFinderResultsListingComponent {
  @Input() query: SearchQuery;
  @Input() serviceRepairs: Array<ServiceRepairResult>;
  @Input() state: State = State.Empty;
  @Input() activeServiceRepair: ServiceRepairResult;
  @Input() errorMessage: string;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  @Input() totalServiceRepairs = 0;
  @Output() serviceRepairMouseEnter = new EventEmitter<ServiceRepairResult>();
  @Output() serviceRepairMouseLeave = new EventEmitter<ServiceRepairResult>();
  @Output() filtersChanged = new EventEmitter<SearchQuery>();
  @Output() pageChanged = new EventEmitter<PageEvent>();

  @ViewChild('serviceRepairListEl') serviceRepairListEl: ElementRef;
  @ViewChildren('serviceRepairEl', { read: ElementRef }) serviceRepairEls: QueryList<ElementRef>;
  @ViewChild('serviceRepairFiltersPopover') serviceRepairFiltersPopover: SatPopover;

  constructor(private _router: Router) {}

  /**
   * Records the current search results URL before navigating to a business's detail page, so that page's
   * "Back" button can return to these exact results (see BusinessPageComponent.goBack). Also clears the
   * airport back-target so the business opened from search doesn't fall back to "Back to <airport>".
   */
  onServiceRepairSelected() {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
  }

  onServiceRepairHover(serviceRepair: ServiceRepairResult, hover: boolean) {
    if (hover) {
      this.serviceRepairMouseEnter.emit(serviceRepair);
    } else {
      this.serviceRepairMouseLeave.emit(serviceRepair);
    }
  }

  onFiltersChanged() {
    this.filtersChanged.emit(this.query);
  }

  /**
   * Returns a jQuery element container for the given business.
   */
  getServiceRepairEl(serviceRepair: ServiceRepairResult) {
    const i = this.serviceRepairs.indexOf(serviceRepair);
    if (i < 0) {
      return null;
    }
    const el = this.serviceRepairEls.toArray()[i];
    // @ts-ignore
    return $(el.nativeElement);
  }

  /**
   * Scrolls the list to the specified business (used for map hover sync).
   */
  public scrollToServiceRepair(serviceRepair: ServiceRepairResult) {
    if (!this.serviceRepairListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.serviceRepairListEl.nativeElement);
    const $el = this.getServiceRepairEl(serviceRepair);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: position }, 500);
  }

  onPageChanged(e: PageEvent) {
    this.pageChanged.emit(e);
    if (!this.serviceRepairListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.serviceRepairListEl.nativeElement);
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: 0 }, 500);
  }
}
